import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FileUp, Loader2, Download, TrendingUp, TrendingDown, AlertTriangle, 
         ChevronDown, ChevronUp, FileSpreadsheet, BarChart3, Users, Building2,
         XCircle, RefreshCw, Search, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LargePdfAnalysis = () => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('price_diff');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeTab, setActiveTab] = useState('items'); // items | suppliers
  const [supplierSearch, setSupplierSearch] = useState('');
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Poll task status
  useEffect(() => {
    if (taskId && taskStatus?.status !== 'completed' && taskStatus?.status !== 'failed') {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const response = await fetch(`${API_URL}/api/analysis/task/${taskId}`);
          const data = await response.json();
          setTaskStatus(data);
          
          if (data.status === 'completed') {
            setResult(data.result);
            clearInterval(pollIntervalRef.current);
            toast.success('تم اكتمال التحليل بنجاح!');
          } else if (data.status === 'failed') {
            clearInterval(pollIntervalRef.current);
            toast.error(data.error || 'فشل التحليل');
          }
        } catch (error) {
          console.error('Error polling task:', error);
        }
      }, 3000);
      
      return () => clearInterval(pollIntervalRef.current);
    }
  }, [taskId, taskStatus?.status]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    const fileName = droppedFile?.name?.toLowerCase() || '';
    const isPdf = droppedFile?.type === 'application/pdf' || fileName.endsWith('.pdf');
    const isExcel = droppedFile?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                   droppedFile?.type === 'application/vnd.ms-excel' ||
                   fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (isPdf || isExcel) {
      setFile(droppedFile);
      setResult(null);
      setTaskId(null);
      setTaskStatus(null);
    } else {
      toast.error('يرجى رفع ملف PDF أو Excel فقط');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    const fileName = selectedFile?.name?.toLowerCase() || '';
    const isPdf = selectedFile?.type === 'application/pdf' || fileName.endsWith('.pdf');
    const isExcel = selectedFile?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                   selectedFile?.type === 'application/vnd.ms-excel' ||
                   fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
    
    if (isPdf || isExcel) {
      setFile(selectedFile);
      setResult(null);
      setTaskId(null);
      setTaskStatus(null);
    } else {
      toast.error('يرجى اختيار ملف PDF أو Excel فقط');
    }
  };

  const startAnalysis = async () => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/analysis/large-pdf/analyze`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTaskId(data.task_id);
        setTaskStatus({ status: 'pending', progress: 0, message: 'جاري بدء التحليل...' });
        toast.info('تم بدء التحليل في الخلفية');
      } else {
        toast.error(data.detail || 'فشل بدء التحليل');
      }
    } catch (error) {
      toast.error('خطأ في الاتصال بالخادم');
    }
  };

  const exportToExcel = async () => {
    if (!result) return;
    
    setIsExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/analysis/large-pdf/export-excel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          analysis: result.analysis,
          filename: file?.name || 'تحليل',
          total_pages: result.total_pages,
          total_records: result.total_records
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `تحليل_شامل_${new Date().toLocaleDateString('ar-SA').replace(/\//g, '-')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('تم تحميل ملف Excel الشامل بنجاح');
      } else {
        toast.error('فشل تصدير الملف');
      }
    } catch (error) {
      toast.error('خطأ في تصدير الملف');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleExpand = (code) => {
    setExpandedItems(prev => ({ ...prev, [code]: !prev[code] }));
  };

  const toggleSupplierExpand = (name) => {
    setExpandedSuppliers(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const resetAnalysis = () => {
    setFile(null);
    setResult(null);
    setTaskId(null);
    setTaskStatus(null);
    setExpandedItems({});
    setExpandedSuppliers({});
    setActiveTab('items');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Filter and sort items
  const getFilteredItems = () => {
    if (!result?.analysis?.items_analysis) return [];
    let items = [...result.analysis.items_analysis];
    if (searchTerm) {
      items = items.filter(item => 
        item.code.includes(searchTerm) ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    items.sort((a, b) => {
      const multiplier = sortOrder === 'desc' ? -1 : 1;
      return multiplier * (a[sortBy] - b[sortBy]);
    });
    return items;
  };

  // Get suppliers sorted
  const getSortedSuppliers = () => {
    if (!result?.analysis?.suppliers_analysis) return [];
    let suppliers = Object.entries(result.analysis.suppliers_analysis);
    if (supplierSearch) {
      suppliers = suppliers.filter(([name]) => 
        name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
    }
    return suppliers.sort((a, b) => b[1].total_amount - a[1].total_amount);
  };

  const filteredItems = getFilteredItems();
  const sortedSuppliers = getSortedSuppliers();

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 className="w-8 h-8" />
          <h1 className="text-2xl font-bold">تحليل ملفات PDF و Excel الكبيرة</h1>
        </div>
        <p className="text-blue-100">
          تحليل شامل للمشتريات مع فروقات الأسعار وتحليل الموردين والتوفير المحتمل
        </p>
      </div>

      {/* Upload Area */}
      {!taskId && !result && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${isDragging ? 'border-blue-500 bg-blue-50' : file ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
          data-testid="pdf-upload-area"
        >
          <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.xls" onChange={handleFileSelect} className="hidden" data-testid="pdf-file-input" />
          
          {file ? (
            <div className="space-y-3">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <FileSpreadsheet className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button onClick={(e) => { e.stopPropagation(); startAnalysis(); }}
                className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium inline-flex items-center gap-2"
                data-testid="start-analysis-btn">
                <TrendingUp className="w-5 h-5" />
                بدء التحليل الشامل
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <FileUp className={`w-8 h-8 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-700">اسحب ملف PDF أو Excel هنا أو انقر للاختيار</p>
                <p className="text-sm text-gray-500 mt-1">يدعم ملفات PDF و Excel الكبيرة (حتى 100 MB)</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Progress Section */}
      {taskStatus && taskStatus.status !== 'completed' && taskStatus.status !== 'failed' && (
        <div className="bg-white rounded-xl border shadow-sm p-6" data-testid="analysis-progress">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">جاري التحليل الشامل...</h3>
              <p className="text-sm text-gray-500">{taskStatus.message}</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">التقدم</span>
              <span className="font-medium text-blue-600">{taskStatus.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="bg-blue-600 h-3 rounded-full transition-all duration-500" style={{ width: `${taskStatus.progress}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {taskStatus?.status === 'failed' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="analysis-error">
          <div className="flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-500" />
            <div>
              <h3 className="font-medium text-red-800">فشل التحليل</h3>
              <p className="text-sm text-red-600">{taskStatus.error || 'حدث خطأ أثناء التحليل'}</p>
            </div>
          </div>
          <button onClick={resetAnalysis} className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            إعادة المحاولة
          </button>
        </div>
      )}

      {/* Results Section */}
      {result && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl border p-4" data-testid="stat-pages">
              <p className="text-sm text-gray-500">الصفحات</p>
              <p className="text-2xl font-bold text-gray-900">{result.total_pages?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border p-4" data-testid="stat-records">
              <p className="text-sm text-gray-500">السجلات</p>
              <p className="text-2xl font-bold text-gray-900">{result.total_records?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border p-4" data-testid="stat-items">
              <p className="text-sm text-gray-500">الأصناف</p>
              <p className="text-2xl font-bold text-gray-900">{result.unique_items?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border p-4" data-testid="stat-suppliers">
              <p className="text-sm text-gray-500">الموردين</p>
              <p className="text-2xl font-bold text-blue-600">{result.analysis?.summary?.suppliers_count?.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border p-4" data-testid="stat-amount">
              <p className="text-sm text-gray-500">إجمالي المبالغ</p>
              <p className="text-xl font-bold text-green-600">{(result.analysis?.summary?.total_amount || 0).toLocaleString()} ر.س</p>
            </div>
          </div>

          {/* Savings/Loss Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingDown className="w-6 h-6 text-green-600" />
                <p className="text-sm text-green-700">إجمالي التوفير الحقيقي</p>
              </div>
              <p className="text-2xl font-bold text-green-700">
                {(result.analysis?.summary?.total_real_savings || 0).toLocaleString(undefined, {minimumFractionDigits: 2})} ر.س
              </p>
              <p className="text-xs text-green-600 mt-1">شراء بأقل من أول سعر</p>
            </div>
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-red-600" />
                <p className="text-sm text-red-700">إجمالي الخسارة الحقيقية</p>
              </div>
              <p className="text-2xl font-bold text-red-700">
                {(result.analysis?.summary?.total_real_loss || 0).toLocaleString(undefined, {minimumFractionDigits: 2})} ر.س
              </p>
              <p className="text-xs text-red-600 mt-1">ارتفاع السعر بعد نزوله</p>
            </div>
            <div className={`border rounded-xl p-6 ${(result.analysis?.summary?.net_benefit || 0) >= 0 
              ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' 
              : 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className={`w-6 h-6 ${(result.analysis?.summary?.net_benefit || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`} />
                <p className={`text-sm ${(result.analysis?.summary?.net_benefit || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                  صافي الفائدة
                </p>
              </div>
              <p className={`text-2xl font-bold ${(result.analysis?.summary?.net_benefit || 0) >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                {(result.analysis?.summary?.net_benefit || 0).toLocaleString(undefined, {minimumFractionDigits: 2})} ر.س
              </p>
              <p className={`text-xs mt-1 ${(result.analysis?.summary?.net_benefit || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                التوفير - الخسارة
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button onClick={exportToExcel} disabled={isExporting}
              className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="export-excel-btn">
              {isExporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
              تصدير Excel شامل (كل التفاصيل + الموردين)
            </button>
            <button onClick={resetAnalysis}
              className="bg-gray-600 text-white px-5 py-2.5 rounded-lg hover:bg-gray-700 transition-colors font-medium inline-flex items-center gap-2"
              data-testid="new-analysis-btn">
              <RefreshCw className="w-5 h-5" />
              تحليل جديد
            </button>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="flex border-b">
              <button onClick={() => setActiveTab('items')}
                className={`flex-1 px-6 py-3 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'items' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Package className="w-5 h-5" />
                الأصناف ({result.analysis?.items_with_price_changes || 0})
              </button>
              <button onClick={() => setActiveTab('suppliers')}
                className={`flex-1 px-6 py-3 font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'suppliers' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}>
                <Building2 className="w-5 h-5" />
                الموردين ({result.analysis?.summary?.suppliers_count || 0})
              </button>
            </div>

            {/* Items Tab */}
            {activeTab === 'items' && (
              <div>
                {/* Search and Sort */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="text" placeholder="بحث بالكود أو الاسم..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div className="flex gap-2">
                      <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                        className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500">
                        <option value="price_diff">فرق السعر</option>
                        <option value="percent_change">نسبة التغيير</option>
                        <option value="total_amount">إجمالي المبلغ</option>
                        <option value="occurrences">التكرار</option>
                      </select>
                      <button onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')} className="border rounded-lg px-3 py-2 hover:bg-gray-50">
                        {sortOrder === 'desc' ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Items List */}
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {filteredItems.slice(0, 100).map((item) => (
                    <div key={item.code} className="hover:bg-gray-50">
                      <div className="p-4 cursor-pointer" onClick={() => toggleExpand(item.code)}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{item.code}</span>
                              <span className="text-sm text-gray-500">{item.unit}</span>
                            </div>
                            <p className="font-medium text-gray-900 truncate">{item.name}</p>
                          </div>
                          <div className="text-left space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-500">فرق:</span>
                              <span className="font-bold text-orange-600">{item.price_diff?.toLocaleString()} ر.س</span>
                            </div>
                            <span className={`text-sm font-medium px-2 py-0.5 rounded ${item.percent_change > 50 ? 'bg-red-100 text-red-700' : item.percent_change > 20 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {item.percent_change?.toFixed(1)}%
                            </span>
                          </div>
                          <button className="p-1 hover:bg-gray-100 rounded">
                            {expandedItems[item.code] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                          </button>
                        </div>
                        <div className="flex gap-4 mt-2 text-sm">
                          <span className="text-green-600">أقل: {item.min_price?.toLocaleString()} ر.س</span>
                          <span className="text-red-600">أعلى: {item.max_price?.toLocaleString()} ر.س</span>
                          <span className="text-gray-500">إجمالي: {item.total_amount?.toLocaleString()} ر.س</span>
                        </div>
                      </div>

                      {/* Expanded - Suppliers Stats */}
                      {expandedItems[item.code] && item.suppliers_stats && (
                        <div className="px-4 pb-4 bg-gray-50">
                          <h4 className="font-medium text-gray-700 mb-2">تحليل الموردين لهذا الصنف</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-gray-100">
                                  <th className="text-right py-2 px-3">المورد</th>
                                  <th className="text-right py-2 px-3">الكمية</th>
                                  <th className="text-right py-2 px-3">متوسط السعر</th>
                                  <th className="text-right py-2 px-3">فرق عن الأرخص</th>
                                  <th className="text-right py-2 px-3">الخسارة</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {item.suppliers_stats.map((ss, idx) => (
                                  <tr key={idx} className="hover:bg-white">
                                    <td className="py-2 px-3">{ss.supplier}</td>
                                    <td className="py-2 px-3">{ss.quantity?.toLocaleString()}</td>
                                    <td className="py-2 px-3">{ss.avg_price?.toLocaleString()} ر.س</td>
                                    <td className="py-2 px-3">
                                      {ss.min_price_diff > 0 ? (
                                        <span className="text-red-600">+{ss.min_price_diff?.toLocaleString()}</span>
                                      ) : (
                                        <span className="text-green-600">أرخص سعر</span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3">
                                      {ss.potential_savings > 0 ? (
                                        <span className="text-red-600 font-medium">{ss.potential_savings?.toLocaleString()} ر.س</span>
                                      ) : (
                                        <span className="text-green-600">0</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Suppliers Tab */}
            {activeTab === 'suppliers' && (
              <div>
                {/* Search */}
                <div className="p-4 border-b bg-gray-50">
                  <div className="relative">
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" placeholder="بحث عن مورد..." value={supplierSearch}
                      onChange={(e) => setSupplierSearch(e.target.value)}
                      className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>

                {/* Suppliers List */}
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {sortedSuppliers.map(([name, data], idx) => (
                    <div key={name} className="hover:bg-gray-50">
                      <div className="p-4 cursor-pointer" onClick={() => toggleSupplierExpand(name)}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{name}</p>
                              <p className="text-sm text-gray-500">{data.items_count} صنف</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-left">
                              <p className="text-xs text-gray-500">المشتريات</p>
                              <p className="font-bold text-gray-700">{data.total_amount?.toLocaleString()} ر.س</p>
                            </div>
                            <div className="text-left">
                              <p className="text-xs text-green-600">التوفير</p>
                              <p className="font-bold text-green-600">{(data.real_savings || 0)?.toLocaleString()} ر.س</p>
                            </div>
                            <div className="text-left">
                              <p className="text-xs text-red-600">الخسارة</p>
                              <p className="font-bold text-red-600">{(data.real_loss || 0)?.toLocaleString()} ر.س</p>
                            </div>
                            <div className="text-left">
                              <p className="text-xs text-gray-500">الصافي</p>
                              <p className={`font-bold ${(data.net_benefit || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                {(data.net_benefit || 0)?.toLocaleString()} ر.س
                              </p>
                            </div>
                            <button className="p-1 hover:bg-gray-100 rounded">
                              {expandedSuppliers[name] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                            </button>
                          </div>
                        </div>
                        {(data.real_savings > 0 || data.real_loss > 0) && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 flex overflow-hidden">
                              <div className="bg-green-500 h-2" style={{ width: `${Math.min((data.real_savings || 0) / ((data.real_savings || 0) + (data.real_loss || 0) + 1) * 100, 100)}%` }} />
                              <div className="bg-red-500 h-2" style={{ width: `${Math.min((data.real_loss || 0) / ((data.real_savings || 0) + (data.real_loss || 0) + 1) * 100, 100)}%` }} />
                            </div>
                            <span className={`text-sm ${(data.net_benefit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              صافي: {(data.net_benefit || 0) >= 0 ? '+' : ''}{(data.net_benefit || 0)?.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Expanded Items */}
                      {expandedSuppliers[name] && data.items_list && (
                        <div className="px-4 pb-4 bg-gray-50">
                          <h4 className="font-medium text-gray-700 mb-2">أصناف هذا المورد</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-gray-100">
                                  <th className="text-right py-2 px-3">الكود</th>
                                  <th className="text-right py-2 px-3">الصنف</th>
                                  <th className="text-right py-2 px-3">الكمية</th>
                                  <th className="text-right py-2 px-3">أول سعر</th>
                                  <th className="text-right py-2 px-3">متوسط السعر</th>
                                  <th className="text-right py-2 px-3">إجمالي</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {data.items_list.slice(0, 20).map((item, idx) => (
                                  <tr key={idx} className="hover:bg-white">
                                    <td className="py-2 px-3 font-mono text-xs">{item.code}</td>
                                    <td className="py-2 px-3">{item.name?.substring(0, 30)}</td>
                                    <td className="py-2 px-3">{item.total_quantity?.toLocaleString()}</td>
                                    <td className="py-2 px-3 text-gray-600">{item.first_price?.toLocaleString()}</td>
                                    <td className="py-2 px-3">
                                      {item.avg_price < item.first_price ? (
                                        <span className="text-green-600">{item.avg_price?.toLocaleString()}</span>
                                      ) : item.avg_price > item.first_price ? (
                                        <span className="text-red-600">{item.avg_price?.toLocaleString()}</span>
                                      ) : (
                                        <span>{item.avg_price?.toLocaleString()}</span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 font-medium">{item.total_amount?.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {data.items_list.length > 20 && (
                              <p className="text-sm text-gray-500 mt-2 text-center">
                                و {data.items_list.length - 20} صنف آخر... (قم بتصدير Excel للتفاصيل الكاملة)
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LargePdfAnalysis;
